import React, {Suspense} from 'react';
import {createBrowserRouter} from 'react-router-dom';
import HomePage from './pages/HomePage';
import SystemPage from './pages/SystemPage';
import SystemThankYouPage from './pages/SystemThankYouPage';
import UnsubscribePage from './pages/UnsubscribePage';
import SlidesPage from './pages/SlidesPage';
import LegalPrivacyPage from './pages/legal/LegalPrivacyPage';
import LegalTermsPage from './pages/legal/LegalTermsPage';
import LegalGeneralTermsPage from './pages/legal/LegalGeneralTermsPage';
import LegalCourseTermsPage from './pages/legal/LegalCourseTermsPage';
import LegalEasOfferTermsPage from './pages/legal/LegalEasOfferTermsPage';
import Error404 from './pages/common/error/Error404';
import LoadingPage from './pages/common/LoadingPage';
import FunnelRootLayout from './pages/funnel/FunnelRoot';
import * as FunnelComponents from './components/lazy/FunnelComponents';
import * as funnelLoaders from './loaders/funnelLoaders';
import * as lazyActions from './loaders/lazyActions';

export type ActionResponse = {
    body: string
    status: number
}

const withSuspense = (Component: React.ComponentType<any>) => {
    return (props: any = {}) => (
        <Suspense fallback={<LoadingPage minHeight='100vh' styles='pt-16' useGradientBackground={true}/>}>
            <Component {...props} />
        </Suspense>
    );
};

export const router = createBrowserRouter([
    {
        path: '/',
        element: <HomePage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/system',
        element: <SystemPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/system/thank-you',
        element: <SystemThankYouPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/unsubscribe',
        element: <UnsubscribePage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/slides',
        element: <SlidesPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/legal/privacy',
        element: <LegalPrivacyPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/legal/terms',
        element: <LegalTermsPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/legal/terms/general',
        element: <LegalGeneralTermsPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/legal/course-terms',
        element: <LegalCourseTermsPage/>,
        errorElement: <Error404/>,
    },
    {
        // EAS-specific special-offer terms — supplement to /legal/course-terms.
        path: '/legal/eas-offer-terms',
        element: <LegalEasOfferTermsPage/>,
        errorElement: <Error404/>,
    },
    {
        path: '/webinar',
        element: <FunnelRootLayout/>,
        errorElement: <Error404/>,
        loader: funnelLoaders.sessionIdLoader,
        id: 'enWebinarRoot',
        children: [
            {
                index: true,
                element: withSuspense(FunnelComponents.WebinarOptInPage)(),
                action: lazyActions.funnelEnWebinarOptInAction,
            },
            {
                path: 'confirmation/:ksuid',
                element: withSuspense(FunnelComponents.WebinarOptInConfirmationPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.saveEnWebinarQuizResultsAction,
            },
            {
                path: 'confirmation-jit/:ksuid',
                element: withSuspense(FunnelComponents.WebinarOptInJustInTimeConfirmationPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.saveEnWebinarJitQuizResultsAction,
            },
            {
                path: 'purchase/:ksuid',
                element: withSuspense(FunnelComponents.WebinarPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseEnWebinarAction,
            },
            {
                // Replay without scrub controls (mirrors PL `/webinar/powtorka/:ksuid`).
                path: 'replay/:ksuid',
                element: withSuspense(FunnelComponents.WebinarReplayPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseEnWebinarAction,
            },
            {
                // Full replay with scrub allowed (mirrors PL `/webinar/powtorka-szkolenia/:ksuid`).
                path: 'full-replay/:ksuid',
                element: withSuspense(FunnelComponents.WebinarFullReplayPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseEnWebinarAction,
            },
            {
                path: 'purchase/success/:ksuid',
                element: withSuspense(FunnelComponents.WebinarSuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: 'purchase/error/:ksuid',
                element: withSuspense(FunnelComponents.WebinarUnsuccessfulPurchasePage)(),
                loader: funnelLoaders.webinarDetailsLoader,
            },
            {
                path: ':ksuid',
                element: withSuspense(FunnelComponents.WebinarPage)(),
                loader: funnelLoaders.webinarDetailsLoader,
                action: lazyActions.purchaseEnWebinarAction,
            },
        ],
    },
    {
        path: '*',
        element: <Error404/>,
    },
]);
